import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, X, Share, Plus } from "lucide-react";
import { usePWAInstall } from "../lib/usePWAInstall";
import { track } from "../lib/track";

/**
 * InstallBanner — small floating "Install MoodCard" pill + iOS-specific hint.
 *
 * iter-77: rebuilt to cover both install paths:
 *   • Chrome / Edge / Android (canInstall + has deferred prompt) →
 *     tapping "install" invokes the native prompt.
 *   • iOS Safari (isIosInstall, no deferred prompt available) →
 *     tapping "install" opens an instructional sheet ("Share → Add to
 *     Home Screen → Add").
 *
 * Dismissal is sticky for 7 days (managed inside the hook). If the user
 * actually installs, we never show it again.
 */
export default function InstallBanner() {
    const { canInstall, isIosInstall, install, dismiss } = usePWAInstall();
    const [iosHint, setIosHint] = useState(false);

    const handleInstallTap = () => {
        if (isIosInstall) {
            track("pwa_ios_hint_opened");
            setIosHint(true);
            return;
        }
        track("pwa_install_click");
        install();
    };

    return (
        <>
            <AnimatePresence>
                {canInstall && !iosHint && (
                    <motion.div
                        initial={{ y: 80, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 80, opacity: 0 }}
                        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                        className="fixed left-4 right-4 z-40 max-w-md mx-auto pointer-events-none"
                        style={{ bottom: "calc(7.5rem + env(safe-area-inset-bottom))" }}
                        data-testid="install-banner"
                    >
                        <div className="pointer-events-auto relative rounded-2xl border border-app/30 bg-app-2 backdrop-blur-xl shadow-[0_20px_60px_rgba(0,0,0,0.5)] p-4 pr-3 flex items-center gap-3">
                            <div className="h-10 w-10 shrink-0 rounded-xl bg-surface-2 border border-app/30 flex items-center justify-center">
                                <Download className="w-4 h-4 text-app" strokeWidth={1.5} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="font-sans text-sm text-app truncate">
                                    {isIosInstall ? "add moodcard to your home screen" : "install moodcard"}
                                </p>
                                <p className="font-sans text-[11px] text-app-3 mt-0.5">
                                    {isIosInstall ? "fullscreen · no browser bar · 3 taps" : "faster · fullscreen · no app store"}
                                </p>
                            </div>
                            <button
                                onClick={handleInstallTap}
                                data-testid="install-banner-install"
                                className="px-4 py-2 rounded-full bg-app text-app-inverse font-sans text-xs font-medium tracking-wide hover:opacity-90 transition-opacity"
                                style={{ backgroundColor: "var(--app-text)" }}
                            >
                                {isIosInstall ? "how" : "install"}
                            </button>
                            <button
                                onClick={dismiss}
                                data-testid="install-banner-dismiss"
                                aria-label="Dismiss"
                                className="p-2 rounded-full text-app-3 hover:text-app hover:bg-surface-2 transition-colors"
                            >
                                <X className="w-3.5 h-3.5" strokeWidth={1.5} />
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* iOS install instructional sheet */}
            <AnimatePresence>
                {iosHint && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setIosHint(false)}
                            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm"
                            data-testid="install-ios-backdrop"
                        />
                        <motion.div
                            initial={{ y: "100%" }}
                            animate={{ y: 0 }}
                            exit={{ y: "100%" }}
                            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                            className="fixed left-0 right-0 bottom-0 z-50 pointer-events-none"
                            data-testid="install-ios-sheet"
                        >
                            <div className="pointer-events-auto mx-auto w-full max-w-md rounded-t-3xl bg-[#0f0f12] border-t border-white/10 shadow-2xl overflow-hidden">
                                <div className="px-5 pt-5 pb-2 flex items-start justify-between">
                                    <div>
                                        <div className="text-[10px] tracking-[0.22em] uppercase text-amber-300/85">add to home screen</div>
                                        <div className="font-display text-xl text-white/95 mt-1">install moodcard</div>
                                    </div>
                                    <button
                                        onClick={() => setIosHint(false)}
                                        aria-label="close"
                                        className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/85"
                                        data-testid="install-ios-close"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                                <ol className="px-5 pb-3 space-y-3 mt-2">
                                    <li className="flex items-start gap-3 text-white/85" data-testid="ios-step-1">
                                        <span className="w-7 h-7 shrink-0 rounded-full bg-white/8 border border-white/10 flex items-center justify-center text-[12px] font-medium">1</span>
                                        <div className="flex-1 text-sm leading-snug">
                                            tap the
                                            <span className="inline-flex items-center gap-1 mx-1.5 px-1.5 py-0.5 rounded-md bg-white/8 border border-white/10 align-middle">
                                                <Share className="w-3 h-3" strokeWidth={1.8} />
                                            </span>
                                            share icon at the bottom of safari
                                        </div>
                                    </li>
                                    <li className="flex items-start gap-3 text-white/85" data-testid="ios-step-2">
                                        <span className="w-7 h-7 shrink-0 rounded-full bg-white/8 border border-white/10 flex items-center justify-center text-[12px] font-medium">2</span>
                                        <div className="flex-1 text-sm leading-snug">
                                            scroll and pick
                                            <span className="inline-flex items-center gap-1 mx-1.5 px-1.5 py-0.5 rounded-md bg-white/8 border border-white/10 align-middle">
                                                <Plus className="w-3 h-3" strokeWidth={1.8} />
                                                <span className="text-[11px]">add to home screen</span>
                                            </span>
                                        </div>
                                    </li>
                                    <li className="flex items-start gap-3 text-white/85" data-testid="ios-step-3">
                                        <span className="w-7 h-7 shrink-0 rounded-full bg-white/8 border border-white/10 flex items-center justify-center text-[12px] font-medium">3</span>
                                        <div className="flex-1 text-sm leading-snug">
                                            tap <strong className="font-semibold">add</strong>. moodcard is now on your phone — opens full-screen, no browser bar.
                                        </div>
                                    </li>
                                </ol>
                                <div className="px-5 pb-6">
                                    <button
                                        type="button"
                                        onClick={() => { setIosHint(false); dismiss(); }}
                                        className="w-full py-3 rounded-full bg-amber-400 hover:bg-amber-300 text-black font-medium text-sm tracking-wide transition-colors"
                                        data-testid="install-ios-done"
                                    >
                                        got it
                                    </button>
                                </div>
                                <div className="pb-[max(env(safe-area-inset-bottom),0rem)]" />
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </>
    );
}
